import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Alert,
  CircularProgress,
  SelectChangeEvent,
  TextField
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { currencyService } from '../../services/currencyService';
import {
  OrganizationCurrencySettings,
  SUPPORTED_CURRENCIES
} from '../../types/currency';

interface CurrencySettingsProps {
  organizationId: string;
}

export const CurrencySettings: React.FC<CurrencySettingsProps> = ({ organizationId }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [settings, setSettings] = useState<OrganizationCurrencySettings | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadSettings();
  }, [organizationId]);

  const loadSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      const orgSettings = await currencyService.getOrganizationCurrencySettings(organizationId);
      setSettings(orgSettings);
    } catch (err) {
      setError(t('errors.loadSettings'));
      console.error('Erreur lors du chargement des paramètres:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCurrencyChange = (event: SelectChangeEvent<string>) => {
    if (!settings) return;
    const newCurrencyCode = event.target.value;
    const newCurrency = SUPPORTED_CURRENCIES.find(c => c.code === newCurrencyCode);
    if (!newCurrency) return;

    setSettings({
      ...settings,
      defaultCurrency: newCurrency.code,
      decimalSeparator: newCurrency.decimalSeparator,
      thousandsSeparator: newCurrency.thousandsSeparator,
      symbolPosition: newCurrency.position
    });
  };

  const handleSeparatorChange = (
    field: 'decimalSeparator' | 'thousandsSeparator',
    value: string
  ) => {
    if (!settings) return;
    setSettings({
      ...settings,
      [field]: value
    });
  };

  const handlePositionChange = (event: SelectChangeEvent<'before' | 'after'>) => {
    if (!settings) return;
    setSettings({
      ...settings,
      symbolPosition: event.target.value as 'before' | 'after'
    });
  };

  const handleSave = async () => {
    if (!settings) return;
    try {
      setSaving(true);
      setError(null);
      await currencyService.updateOrganizationCurrencySettings(organizationId, settings);
    } catch (err) {
      setError(t('errors.saveSettings'));
      console.error('Erreur lors de la sauvegarde des paramètres:', err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  if (!settings) {
    return (
      <Alert severity="error">
        {t('errors.settingsNotFound')}
      </Alert>
    );
  }

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {t('organization.currencySettings')}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <FormControl fullWidth margin="normal">
          <InputLabel>{t('currency.defaultCurrency')}</InputLabel>
          <Select
            value={settings.defaultCurrency}
            onChange={handleCurrencyChange}
            label={t('currency.defaultCurrency')}
          >
            {SUPPORTED_CURRENCIES.map((currency) => (
              <MenuItem key={currency.code} value={currency.code}>
                {currency.name} ({currency.symbol})
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth margin="normal">
          <TextField
            label={t('currency.decimalSeparator')}
            value={settings.decimalSeparator}
            onChange={(e) => handleSeparatorChange('decimalSeparator', e.target.value)}
            inputProps={{ maxLength: 1 }}
          />
        </FormControl>

        <FormControl fullWidth margin="normal">
          <TextField
            label={t('currency.thousandsSeparator')}
            value={settings.thousandsSeparator}
            onChange={(e) => handleSeparatorChange('thousandsSeparator', e.target.value)}
            inputProps={{ maxLength: 1 }}
          />
        </FormControl>

        <FormControl fullWidth margin="normal">
          <InputLabel>{t('currency.symbolPosition')}</InputLabel>
          <Select
            value={settings.symbolPosition}
            onChange={handlePositionChange}
            label={t('currency.symbolPosition')}
          >
            <MenuItem value="before">{t('currency.symbolBefore')}</MenuItem>
            <MenuItem value="after">{t('currency.symbolAfter')}</MenuItem>
          </Select>
        </FormControl>

        <Box sx={{ mt: 3 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              t('common.save')
            )}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};